// Modular tracking layer — GTM + GA4 + Meta Pixel + Google Ads.
// Scripts inject ONLY when (a) the corresponding env ID is present AND
// (b) the user has given the matching cookie consent. This keeps the site
// clean and GDPR / Garante Privacy compliant: no analytics/marketing cookies
// are set before consent.

import { getConsent, CONSENT_EVENT } from "./consent";

const GTM_ID = process.env.REACT_APP_GTM_ID;
const GA4_ID = process.env.REACT_APP_GA4_ID;
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;
const ADS_LABEL = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;

let gtmLoaded = false;
let gaLoaded = false;
let marketingLoaded = false;
let gtagBaseLoaded = false;
let listenerAttached = false;

function addScript(src, attrs = {}) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  return s;
}

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
}

function ensureGtagBase(id) {
  if (gtagBaseLoaded || !id) return;
  gtagBaseLoaded = true;
  addScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
}

function loadGTM() {
  if (gtmLoaded || !GTM_ID) return;
  gtmLoaded = true;
  ensureDataLayer();
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  addScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
}

function loadAnalytics() {
  if (gaLoaded || !GA4_ID) return;
  gaLoaded = true;
  ensureDataLayer();
  ensureGtagBase(GA4_ID);
  window.gtag("config", GA4_ID, { send_page_view: true });
}

function loadMarketing() {
  if (marketingLoaded) return;
  ensureDataLayer();
  // Google Ads
  if (ADS_ID) {
    ensureGtagBase(ADS_ID);
    window.gtag("config", ADS_ID);
  }
  // Meta Pixel
  if (PIXEL_ID) {
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq("init", PIXEL_ID);
    window.fbq("track", "PageView");
  }
  marketingLoaded = !!(ADS_ID || PIXEL_ID);
}

// Load only the categories the user consented to. Idempotent.
export function applyConsent(consent) {
  if (typeof window === "undefined" || !consent) return;
  ensureDataLayer();
  if (consent.analytics || consent.marketing) loadGTM();
  if (consent.analytics) loadAnalytics();
  if (consent.marketing) loadMarketing();
}

export function initTracking() {
  if (typeof window === "undefined") return;
  ensureDataLayer();
  if (!listenerAttached) {
    listenerAttached = true;
    window.addEventListener(CONSENT_EVENT, (e) => applyConsent(e.detail || getConsent()));
  }
  const consent = getConsent();
  if (consent) applyConsent(consent);
}

// Central event dispatcher -> dataLayer + GA4 + Meta Pixel.
// gtag/fbq exist only after consent, so these no-op until the user opts in.
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...params });
  if (window.gtag) window.gtag("event", name, params);
  if (window.fbq) {
    const std = { Lead: 1, Contact: 1, SubmitApplication: 1 };
    if (std[params.fb_event]) window.fbq("track", params.fb_event, params);
    else window.fbq("trackCustom", name, params);
  }
}

export function trackPageView(path) {
  if (window.gtag && GA4_ID) window.gtag("event", "page_view", { page_path: path });
  if (window.fbq) window.fbq("track", "PageView");
  if (window.dataLayer) window.dataLayer.push({ event: "page_view", page_path: path });
}

// --- Semantic helpers ---
export const track = {
  whatsappClick: (location) => trackEvent("whatsapp_click", { location, fb_event: "Contact" }),
  ctaClick: (label, location) => trackEvent("cta_click", { label, location }),
  emailClick: (location) => trackEvent("email_click", { location, fb_event: "Contact" }),
  phoneClick: (location) => trackEvent("phone_click", { location, fb_event: "Contact" }),
  photoUpload: (count) => trackEvent("photo_upload", { count }),
  formStart: () => trackEvent("form_start", {}),
  formStep: (step) => trackEvent("form_step", { step }),
  formSubmit: (data) => {
    trackEvent("generate_lead", { fb_event: "Lead", ...data });
    // Google Ads conversion
    if (window.gtag && ADS_ID && ADS_LABEL) {
      window.gtag("event", "conversion", { send_to: `${ADS_ID}/${ADS_LABEL}` });
    }
  },
  scrollDepth: (percent) => trackEvent("scroll_depth", { percent }),
  timeOnPage: (seconds) => trackEvent("time_on_page", { seconds }),
};
